import React, { useState, useEffect } from 'react';
import { FaArrowRight } from 'react-icons/fa';

export default function HeroSection() {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // Auto-cycle background images
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentImageIndex((prevIndex) => (prevIndex + 1) % 5);
        }, 4000); // Change image every 4 seconds

        return () => clearInterval(interval);
    }, []);

    const heroHeadings = [
        'KNOWLEDGE TALK MATTERS',
        'EXCEL IN MATHEMATICS',
        'LEARN. PRACTICE. MASTER.',
        'BUILD CONFIDENCE IN MATH',
        'ACHIEVE TOP RESULTS'
    ];

    const heroDescriptions = [
        'Learn, practice, and master mathematics with expert guidance and clear explanations.',
        'Step-by-step concepts, solved examples, and weekly practice to boost your grades.',
        'Daily drills, past papers, and tips to turn practice into mastery.',
        'From basics to advanced, build strong problem‑solving skills and confidence.',
        'Targeted revision and exam strategies to achieve your best results.'
    ];

    return (
        <section className="relative h-screen overflow-hidden">
            {/* Background Images Slideshow */}
            <div className="absolute inset-0">
                {/* Image 1 - Students in auditorium */}
                <div className={`absolute inset-0 transition-opacity duration-1000 ${currentImageIndex === 0 ? 'opacity-100' : 'opacity-0'}`}>
                    <img
                        src="hero/1.jpg"
                        alt="Students in auditorium"
                        className="w-full h-full object-cover"
                    />
                </div>

                {/* Image 2 - Modern classroom */}
                <div className={`absolute inset-0 transition-opacity duration-1000 ${currentImageIndex === 1 ? 'opacity-100' : 'opacity-0'}`}>
                    <img
                        src="hero/2.jpg"
                        alt="Modern classroom"
                        className="w-full h-full object-cover"
                    />
                </div>

                {/* Image 3 - Students studying */}
                <div className={`absolute inset-0 transition-opacity duration-1000 ${currentImageIndex === 2 ? 'opacity-100' : 'opacity-0'}`}>
                    <img
                        src="hero/3.jpg"
                        alt="Students studying together"
                        className="w-full h-full object-cover"
                    />
                </div>

                {/* Image 4 - Online learning */}
                <div className={`absolute inset-0 transition-opacity duration-1000 ${currentImageIndex === 3 ? 'opacity-100' : 'opacity-0'}`}>
                    <img
                        src="hero/4.jpg"
                        alt="Online learning setup"
                        className="w-full h-full object-cover"
                    />
                </div>

                {/* Image 5 - University campus */}
                <div className={`absolute inset-0 transition-opacity duration-1000 ${currentImageIndex === 4 ? 'opacity-100' : 'opacity-0'}`}>
                    <img
                        src="hero/5.jpg"
                        alt="University campus"
                        className="w-full h-full object-cover"
                    />
                </div>

                <div className="absolute inset-0 bg-black/40"></div>
            </div>

            {/* Hero Content - Modern Design */}
            <div className="relative z-10 flex items-center justify-center h-full">
                <div className="text-center px-4 w-full max-w-5xl">

                    <div className="inline-block bg-blue-500/20 px-4 py-1 rounded-full mb-6">
                        <span className="text-blue-200 text-sm font-medium uppercase tracking-wider">Welcome to Sudesh Maths</span>
                    </div>
                    <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight lemon-milk bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-100">
                        {heroHeadings[currentImageIndex]}
                    </h1>
                    <p className="text-blue-100/90 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed poppins mb-8">
                        {heroDescriptions[currentImageIndex]}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-lg transition-all duration-300 transform hover:-translate-y-1 shadow-lg">
                            Get Started
                        </button>
                        <button className="bg-transparent hover:bg-white/10 text-white border-2 border-white/20 font-semibold px-8 py-3 rounded-lg transition-all duration-300 transform hover:-translate-y-1">
                            Learn More
                        </button>
                    </div>

                </div>
            </div>

            {/* Navigation Arrows */}
            <button
                onClick={() => setCurrentImageIndex((prev) => (prev - 1 + 5) % 5)}
                className="absolute left-8 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center text-white hover:bg-white/30 transition-colors"
            >
                <FaArrowRight className="rotate-180 text-sm" />
            </button>
            <button
                onClick={() => setCurrentImageIndex((prev) => (prev + 1) % 5)}
                className="absolute right-8 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center text-white hover:bg-white/30 transition-colors"
            >
                <FaArrowRight className="text-sm" />
            </button>

            {/* Slide Indicators */}
            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex gap-3">
                <button
                    onClick={() => setCurrentImageIndex(0)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${currentImageIndex === 0 ? 'bg-white scale-125' : 'bg-white/50 hover:bg-white/75'}`}
                ></button>
                <button
                    onClick={() => setCurrentImageIndex(1)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${currentImageIndex === 1 ? 'bg-white scale-125' : 'bg-white/50 hover:bg-white/75'}`}
                ></button>
                <button
                    onClick={() => setCurrentImageIndex(2)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${currentImageIndex === 2 ? 'bg-white scale-125' : 'bg-white/50 hover:bg-white/75'}`}
                ></button>
                <button
                    onClick={() => setCurrentImageIndex(3)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${currentImageIndex === 3 ? 'bg-white scale-125' : 'bg-white/50 hover:bg-white/75'}`}
                ></button>
                <button
                    onClick={() => setCurrentImageIndex(4)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${currentImageIndex === 4 ? 'bg-white scale-125' : 'bg-white/50 hover:bg-white/75'}`}
                ></button>
            </div>
        </section>
    );
}
